import React, { createContext, ReactNode, useMemo } from 'react';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { defaultBusinessInsightsContextValue } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/context/insightsDefaultValue';
import { useFetchInsightReports } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/hooks/useFetchInsightReports';
import { useFetchSingleInsightsReport } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/hooks/useFetchSingleInsightsReport';
import { BusinessInsightsContextValue } from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/types/businessInsightsContext';
import {
  getRelevantInsightReport,
  mergeReportData,
} from 'src/components/enterprise/features/HomePage/BusinessAdminInsights/utils/insightsProviderUtil';

export const BusinessInsightsContext =
  createContext<BusinessInsightsContextValue | null>(null);

type BusinessInsightsProviderProps = {
  children: ReactNode;
};

export const BusinessInsightsProvider = ({
  children,
}: BusinessInsightsProviderProps): JSX.Element => {
  const { corpId } = useAuth();
  const {
    insightReports,
    isLoading: isInsightReportsLoading,
    hasError: hasInsightReportsError,
  } = useFetchInsightReports(corpId);

  const relevantInsightReport = getRelevantInsightReport(insightReports);

  const {
    singleInsightsReport,
    fetchSingleInsightsReport,
    isLoading: isSingleInsightsReportLoading,
    hasError: hasSingleInsightsReportError,
  } = useFetchSingleInsightsReport(corpId, relevantInsightReport?.uniqueName);

  const value = useMemo<BusinessInsightsContextValue>(
    () => ({
      ...defaultBusinessInsightsContextValue,
      ...mergeReportData(relevantInsightReport, singleInsightsReport),
      isLoading: isInsightReportsLoading || isSingleInsightsReportLoading,
      isError: hasInsightReportsError || hasSingleInsightsReportError,
      applyFilters: fetchSingleInsightsReport,
    }),
    [
      relevantInsightReport,
      singleInsightsReport,
      isInsightReportsLoading,
      isSingleInsightsReportLoading,
      hasInsightReportsError,
      hasSingleInsightsReportError,
      fetchSingleInsightsReport,
    ]
  );

  return (
    <BusinessInsightsContext.Provider value={value}>
      {children}
    </BusinessInsightsContext.Provider>
  );
};
